import { Actions2 } from "@/components/layout-2/actions-2";
import { DynamicHeader } from "@/components/layout-2/dynamic-header";
import { DynamicObjectForm } from "@/components/layout-2/dynamic-object-form";
import { DynamicRelatedObjects } from "@/components/layout-2/dynamic-related-objects";
import { DynamicTabContent } from "@/components/layout-2/dynamic-tab-content";
import { HtmlPreview } from "@/components/layout-2/html-preview";
import { LocationMap } from "@/components/layout-2/location-map";
import { PathLayout, type PathLayoutProps } from "@/components/layout-2/path";
import { VaultPasswordCard } from "@/components/tools/vault/password-card";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { NavTabItem, NavTabs } from "@/components/ui/navigation-tabs";
import { DynamicDetailProvider } from "@/contexts/dynamic-detail";
import { useDynamicDetail } from "@/hooks/use-dynamic-detail";
import { RecentRecordService } from "@/services/recent-record";
import { LAYOUT_AUDIENCES } from "@/lib/navigation";
import { useUser } from "@/providers/user";
import type { HtmlPreviewProps, OptionsResponse } from "@/types/dynamic-detail";
import { resolveDetailTabRedirect, resolveRecordBasePath } from "@/utils/detail-tab-redirect";
import { withoutSectionActions } from "@/utils/section-actions";
import { cn } from "@/lib/utils";
import { navigationTabs as getNavigationTabs, sidePanelTabs as getSidePanelTabs } from "@/utils/detail-tab-placement";
import * as LucideIcons from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLoaderData, useLocation, useNavigate } from "react-router-dom";
import { AudioPlayer2 } from "../layout-2/audio-player-2";
import { VideoPlayer } from "../layout-2/video-player";
import { InteractionsHistory } from "../layout-2/interactions-history";
import { Button } from "../ui/button";
import { AudioProvider } from "@/contexts/audio-context";
import { AlertLayout } from "../layout-2/alerts";

type GenericDetailLayoutProps = {
    objectType: string;
    objectId: string | number;
    breadcrumbs: { name: string; href?: string }[];
    endpointUrl?: string;
    objectTitle?: string;
    extraActions?: React.ReactNode;
};

export function GenericDetailLayout({ objectType, objectId, breadcrumbs, endpointUrl, objectTitle, extraActions }: GenericDetailLayoutProps) {
    const { detailData, loading: dynamicLoading, refreshing, error: dynamicError, refetch } = useDynamicDetail({ objectType, objectId, endpointUrl });
    const { metadata } = useLoaderData() as { metadata: OptionsResponse };
    const location = useLocation();
    const navigate = useNavigate();
    // Recent records feed the quick access, not offered to every audience (partners).
    const { is_visible_to_audience } = useUser();
    const tracksRecentRecords = is_visible_to_audience(LAYOUT_AUDIENCES.quickAccess);
    const [editFormOpen, setEditFormOpen] = useState(false);

    const editFormConfig = metadata.detail_config?.forms?.[0];
    // A form can be reserved to some records (`visibility_conditions`), and the metadata describe
    // the object, not a record: only `detail-data` knows whether this one is offered the form.
    const recordServesEditForm = detailData?.forms === undefined || detailData.forms.length > 0;
    const canEdit = !!editFormConfig && recordServesEditForm && metadata.permissions?.update !== "none";
    const editEndpointUrl = `/crm/objects/${endpointUrl ?? objectType}/`;
    // Objects can expose their detail page outside of `/<object_url>/` (DetailView.base_url),
    // unless the page is opened on the generic route: its tabs then stay there.
    const recordBasePath = resolveRecordBasePath({ pathname: location.pathname, objectType, objectId, detailBaseUrl: metadata.detail_base_url });
    // Prefilled by `detail-data`, which reads the record itself: a form carries controls no
    // section displays — a hidden parent, a source — and a prefill built from the sections would
    // send them back empty, which `form-action` writes as an explicit null.
    const editInitialValues = detailData?.forms?.[0]?.form_values;
    const recordTabs = detailData?.tabs;
    const visibleTabs = recordTabs ?? metadata.detail_config.tabs;
    const visibleRelatedObjects = detailData?.related_objects ?? metadata.detail_config.related_objects;
    // An action a section of the record offers is run from there: the header keeps the others.
    const headerActions = useMemo(() => withoutSectionActions(detailData?.actions ?? [], recordTabs), [detailData?.actions, recordTabs]);
    const hasDetailActions = headerActions.length > 0;
    const showHeaderActions = hasDetailActions || canEdit || !!extraActions;
    // The tab is the first segment under the record: a drill-down (`feeding/runs/12`) keeps its tab.
    const currentTabPath = location.pathname.startsWith(`${recordBasePath}/`) ? location.pathname.slice(recordBasePath.length + 1).split("/")[0] : undefined;
    const navigationTabs = useMemo(() => getNavigationTabs(visibleTabs), [visibleTabs]);
    const sidePanelTabs = useMemo(() => getSidePanelTabs(visibleTabs, currentTabPath), [currentTabPath, visibleTabs]);

    useEffect(() => {
        if (objectTitle) {
            document.title = `${objectTitle} | Walter CRM`;
        }
    }, [objectTitle]);

    // Ajout automatique du recent record
    useEffect(() => {
        const addRecentRecord = async () => {
            try {
                await RecentRecordService.create({
                    reference_type_name: endpointUrl ?? objectType,
                    reference_id: Number(objectId)
                });
            } catch (error) {
                // On ignore silencieusement les erreurs pour ne pas perturber l'UX
                console.debug("Erreur lors de l'ajout du recent record:", error);
            }
        };

        // On ajoute le record seulement si on a un objectId valide
        if (objectId && objectType && tracksRecentRecords) {
            addRecentRecord();
        }
    }, [endpointUrl, objectId, objectType, tracksRecentRecords]);

    // Redirection automatique vers le tab par défaut si on est sur la route de base ou sur un
    // tab masqué pour ce record. On n'utilise que les tabs de `detail-data` : ceux des metadata
    // ignorent les visibility conditions et mèneraient vers un tab que le record ne sert pas.
    React.useEffect(() => {
        const redirectTo = resolveDetailTabRedirect({
            pathname: location.pathname,
            recordBasePath,
            tabs: recordTabs ? getNavigationTabs(recordTabs) : undefined
        });
        if (redirectTo) {
            navigate(redirectTo, { replace: true });
        }
    }, [recordTabs, recordBasePath, location.pathname, navigate]);

    if (dynamicLoading) {
        return (
            <div className="flex items-center justify-center py-8">
                <LucideIcons.Loader className="h-6 w-6 animate-spin text-gray-500" />
                <span className="ml-2 text-gray-500">Loading...</span>
            </div>
        );
    }

    // Sans `detail-data`, tabs et related objects retomberaient sur les metadata, qui ignorent les
    // visibility conditions de ce record : mieux vaut proposer un nouvel essai.
    if (dynamicError && !detailData) {
        return (
            <div className="flex flex-col items-center justify-center gap-2 py-8 text-sm text-gray-500">
                <span>Unable to load this record.</span>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                    <LucideIcons.RotateCw className="mr-1 h-3 w-3" /> Retry
                </Button>
            </div>
        );
    }

    return (
        <AudioProvider>
            <div className="flex flex-col gap-5 pb-11">
                <div className="flex w-full flex-col items-start justify-between gap-4 sm:flex-row sm:items-center lg:col-span-2">
                    {breadcrumbs && (
                        <Breadcrumb>
                            <BreadcrumbList>
                                {breadcrumbs.map((item, index) =>
                                    index < breadcrumbs.length - 1 ? (
                                        <React.Fragment key={`${item.name}_${index}`}>
                                            <BreadcrumbItem>
                                                <BreadcrumbLink href={item.href ?? ""}>{item.name.slice(0, 50)}</BreadcrumbLink>
                                            </BreadcrumbItem>
                                            <BreadcrumbSeparator />
                                        </React.Fragment>
                                    ) : (
                                        <BreadcrumbPage key={`${index}-${item.name}`}>{item.name.slice(0, 50) && item.name.length > 50 ? item.name.slice(0, 50) + "..." : item.name}</BreadcrumbPage>
                                    )
                                )}
                            </BreadcrumbList>
                        </Breadcrumb>
                    )}
                    {showHeaderActions && (
                        <div className="flex flex-row items-center justify-end gap-2">
                            <Button
                                variant="outline"
                                className="size-[36px] rounded-sm border bg-white px-2 shadow-sm"
                                onClick={() => {
                                    refetch();
                                }}
                                disabled={dynamicLoading || refreshing}
                                aria-busy={refreshing}
                            >
                                {/* Turns while the record is fetched again: the page itself stays as it was. */}
                                <LucideIcons.RotateCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
                            </Button>
                            {extraActions}
                            {canEdit && editFormConfig && (
                                <Button variant="outline" className="rounded-sm border bg-white px-3 shadow-sm" onClick={() => setEditFormOpen(true)}>
                                    <LucideIcons.Pencil className="mr-1 h-4 w-4" />
                                    {editFormConfig.update_label}
                                </Button>
                            )}
                            {hasDetailActions && (
                                <Actions2
                                    actions={headerActions}
                                    selectedItemIds={[objectId.toString()]}
                                    selectAll={false}
                                    selectAllCount={0}
                                    endpointUrl={editEndpointUrl}
                                    action_scope={"detail"}
                                    onActionSuccess={() => {
                                        void refetch();
                                    }}
                                />
                            )}
                        </div>
                    )}
                    {canEdit && editFormConfig && (
                        <DynamicObjectForm
                            open={editFormOpen}
                            onOpenChange={setEditFormOpen}
                            mode="update"
                            formConfig={editFormConfig}
                            endpointUrl={editEndpointUrl}
                            recordId={objectId}
                            initialValues={editInitialValues}
                            formIndex={0}
                            onSuccess={() => refetch()}
                        />
                    )}
                </div>
                <div className="flex flex-col-reverse gap-5 lg:grid lg:grid-cols-2">
                    <div className="flex flex-col gap-4 lg:col-span-1">
                        <div className="flex w-full flex-col items-start space-y-2 rounded-md bg-white p-4 shadow-md sm:p-6">
                            {navigationTabs.length > 0 && (
                                <NavTabs defaultValue={navigationTabs.find(tab => tab.default)?.route_path || navigationTabs[0]?.route_path || "informations"}>
                                    {navigationTabs.map(tab => (
                                        <NavTabItem key={tab.name} to={`${recordBasePath}/${tab.route_path}`}>
                                            {tab.label}
                                        </NavTabItem>
                                    ))}
                                </NavTabs>
                            )}
                            <div className="mt-4 w-full">
                                <DynamicDetailProvider detailData={detailData} tabs={visibleTabs} loading={dynamicLoading} error={dynamicError} refetch={refetch} metadata={metadata}>
                                    <Outlet />
                                </DynamicDetailProvider>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2 lg:col-span-1">
                        {detailData?.header && <DynamicHeader header={detailData.header} metadata={metadata} />}
                        {sidePanelTabs.length > 0 && (
                            <DynamicDetailProvider detailData={detailData} tabs={visibleTabs} loading={dynamicLoading} error={dynamicError} refetch={refetch} metadata={metadata}>
                                {sidePanelTabs.map(tab => (
                                    <DynamicTabContent key={tab.route_path} objectType={objectType} objectId={objectId} tab={tab} />
                                ))}
                            </DynamicDetailProvider>
                        )}
                        {detailData?.custom_components?.map((component, idx) => {
                            if (component.type === "path") {
                                const pathProps = component.props as PathLayoutProps;
                                if (!pathProps.current_step) return null;
                                return (
                                    <div key={idx} className="mt-2 flex min-w-0 flex-col items-start space-y-2 rounded-md  bg-white p-6 shadow-md">
                                        <PathLayout {...pathProps} />
                                    </div>
                                );
                            }
                            if (component.type === "audio-player") {
                                const videoUrl = component.props.videoUrl as string | undefined;
                                const audioUrl = component.props.audioUrl as string | undefined;
                                if (!videoUrl && !audioUrl) return null;
                                return (
                                    <div key={idx} className="mt-2 flex  flex-col items-start space-y-2 rounded-md  bg-white p-4 shadow-md">
                                        {videoUrl ? (
                                            <VideoPlayer videoUrl={videoUrl} />
                                        ) : audioUrl ? (
                                            <AudioPlayer2 audioUrl={audioUrl} phases={component.props.phases ?? undefined} />
                                        ) : null}
                                    </div>
                                );
                            }
                            if (component.type === "alerts") {
                                return (
                                    <AlertLayout
                                        key={idx}
                                        alerts={component.props.alerts}
                                        actions={detailData?.actions}
                                        objectId={objectId}
                                        endpointUrl={editEndpointUrl}
                                        onActionSuccess={() => {
                                            void refetch();
                                        }}
                                    />
                                );
                            }
                            if (component.type === "html-preview") {
                                return (
                                    <div key={idx} className="mt-2 flex w-full flex-col items-start rounded-md bg-white p-4 shadow-md">
                                        <HtmlPreview {...(component.props as HtmlPreviewProps)} />
                                    </div>
                                );
                            }
                            if (component.type === "vault-password") {
                                return <VaultPasswordCard key={idx} credentialId={component.props.credentialId} username={component.props.username} />;
                            }
                            if (component.type === "map") {
                                const { latitude, longitude, address, company_name, candidates, candidate_filters, candidate_radius_km } = component.props;
                                // Only render if we have valid coordinates
                                if (latitude && longitude && !isNaN(parseFloat(latitude)) && !isNaN(parseFloat(longitude))) {
                                    return (
                                        <div key={idx} className="mt-2 flex w-full flex-col items-start space-y-2 rounded-md bg-white p-4 shadow-md">
                                            <LocationMap
                                                latitude={parseFloat(latitude)}
                                                longitude={parseFloat(longitude)}
                                                address={address}
                                                company_name={company_name}
                                                candidates={candidates}
                                                candidateFilters={candidate_filters}
                                                candidateRadiusKm={candidate_radius_km}
                                            />
                                        </div>
                                    );
                                }
                                return null;
                            }
                            return null;
                        })}
                        {visibleRelatedObjects && (
                            <DynamicRelatedObjects
                                relatedObjects={visibleRelatedObjects}
                                objectApiUrl={`/crm/objects/${endpointUrl ?? objectType}`}
                                objectId={objectId.toString()}
                                objectName={objectType}
                            />
                        )}
                        {detailData?.interactions_history && (
                            <div className="mt-2 flex w-full flex-col items-start space-y-2 rounded-md  bg-white p-4 shadow-md">
                                <InteractionsHistory
                                    interactions={detailData.interactions_history.interactions}
                                    count={detailData.interactions_history.count}
                                    next={detailData.interactions_history.next}
                                    filters={detailData.interactions_history.filters}
                                    canViewAccountInteractions={detailData.interactions_history.can_view_account_interactions}
                                    noteForm={detailData.interactions_history.note_form}
                                    objectType={endpointUrl ?? objectType}
                                    objectId={objectId}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AudioProvider>
    );
}
